import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Briefcase, Calendar, Camera, DollarSign, Award, 
  Clock, MapPin, CheckCircle, Phone, ChevronRight, BarChart3,
  PieChart, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const StaffPerformanceDashboard = ({ token, onClose }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <CardContent className="p-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl">
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">Unable to load dashboard data</p>
            <Button onClick={onClose} className="mt-4">Close</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, earnings_trend, job_type_breakdown, performance_metrics, recent_jobs, upcoming_schedule } = dashboard;

  // Calculate trend comparison (current vs previous month)
  const currentMonthEarnings = earnings_trend[earnings_trend.length - 1]?.earnings || 0;
  const prevMonthEarnings = earnings_trend[earnings_trend.length - 2]?.earnings || 0;
  const earningsTrend = prevMonthEarnings > 0 
    ? ((currentMonthEarnings - prevMonthEarnings) / prevMonthEarnings * 100).toFixed(1)
    : 0;

  // Find max earnings for chart scaling
  const maxEarnings = Math.max(...earnings_trend.map(m => m.earnings), 1);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'history', label: 'Job History', icon: Clock },
    { id: 'schedule', label: 'Upcoming', icon: Calendar }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="performance-dashboard-modal">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <CardHeader className="rounded-t-[12px] border-b border-gold/25 bg-ink text-cream">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Performance Dashboard
              </CardTitle>
              <p className="text-blue-100 text-sm mt-1">{dashboard.staff_name}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
              data-testid="close-dashboard-btn"
            >
              Close
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-white text-blue-700' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </CardHeader>

        {/* Content */}
        <CardContent className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-600 font-medium">{summary.month_name}</p>
                        <p className="text-2xl font-bold text-green-700">£{summary.current_month_earnings.toFixed(0)}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {parseFloat(earningsTrend) > 0 ? (
                            <ArrowUpRight className="w-3 h-3 text-green-600" />
                          ) : parseFloat(earningsTrend) < 0 ? (
                            <ArrowDownRight className="w-3 h-3 text-red-600" />
                          ) : (
                            <Minus className="w-3 h-3 text-slate-400" />
                          )}
                          <span className={`text-xs ${parseFloat(earningsTrend) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {earningsTrend}% vs last month
                          </span>
                        </div>
                      </div>
                      <DollarSign className="w-8 h-8 text-green-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Total Completed</p>
                        <p className="text-2xl font-bold text-blue-700">{summary.total_completed_jobs}</p>
                        <p className="text-xs text-blue-500 mt-1">jobs all time</p>
                      </div>
                      <Briefcase className="w-8 h-8 text-blue-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 font-medium">All-Time Earnings</p>
                        <p className="text-2xl font-bold text-purple-700">£{summary.total_earnings.toFixed(0)}</p>
                        <p className="text-xs text-purple-500 mt-1">lifetime total</p>
                      </div>
                      <Award className="w-8 h-8 text-purple-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-orange-600 font-medium">Upcoming</p>
                        <p className="text-2xl font-bold text-orange-700">{summary.upcoming_jobs_count}</p>
                        <p className="text-xs text-orange-500 mt-1">jobs scheduled</p>
                      </div>
                      <Calendar className="w-8 h-8 text-orange-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Earnings Trend Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    6-Month Earnings Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between gap-2 h-40">
                    {earnings_trend.map((month, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs font-medium text-slate-600">
                          £{month.earnings.toFixed(0)}
                        </span>
                        <div 
                          className="w-full rounded-t-md bg-gold transition-colors hover:bg-gold-hover"
                          style={{ 
                            height: `${Math.max((month.earnings / maxEarnings) * 100, 4)}%`,
                            minHeight: '8px'
                          }}
                          title={`${month.month} ${month.year}: £${month.earnings.toFixed(2)} (${month.jobs} jobs)`}
                        />
                        <span className="text-xs text-slate-500">{month.month}</span>
                        <span className="text-[10px] text-slate-400">{month.jobs} jobs</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics & Job Types */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Performance Metrics */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Performance Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Check-In Rate</span>
                        <span className="font-medium">{performance_metrics.check_in_rate}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${performance_metrics.check_in_rate}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Client Contact Rate</span>
                        <span className="font-medium">{performance_metrics.client_contact_rate}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${performance_metrics.client_contact_rate}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Photo Upload Rate</span>
                        <span className="font-medium">{performance_metrics.photo_upload_rate}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full transition-all"
                          style={{ width: `${performance_metrics.photo_upload_rate}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Based on {summary.total_completed_jobs} completed jobs
                    </p>
                  </CardContent>
                </Card>

                {/* Job Type Breakdown */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <PieChart className="w-4 h-4 text-purple-600" />
                      Jobs by Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {job_type_breakdown.length > 0 ? (
                      <div className="space-y-2">
                        {job_type_breakdown.slice(0, 5).map((type, index) => {
                          const percentage = (type.count / summary.total_completed_jobs * 100).toFixed(0);
                          const colors = ['bg-gold', 'bg-gold-hover', 'bg-amber-400', 'bg-amber-500', 'bg-ink'];
                          return (
                            <div key={index}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-600 truncate max-w-[60%]">{type.type}</span>
                                <span className="font-medium">{type.count} ({percentage}%)</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2">
                                <div 
                                  className={`${colors[index % colors.length]} h-2 rounded-full transition-all`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-4">No job data available yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Recent Completed Jobs</h3>
              {recent_jobs.length > 0 ? (
                recent_jobs.map((job, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-800">{job.title}</h4>
                            {job.checked_in && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                Checked In
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{job.client_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(job.date).toLocaleDateString('en-GB', { 
                                day: 'numeric', month: 'short', year: 'numeric' 
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {job.location || 'No location'}
                            </span>
                            {job.photos_count > 0 && (
                              <span className="flex items-center gap-1">
                                <Camera className="w-3 h-3" />
                                {job.photos_count} photos
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">£{job.earnings.toFixed(2)}</p>
                          <p className="text-xs text-slate-400">earned</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-slate-400">
                    <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No completed jobs yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Upcoming Schedule</h3>
              {upcoming_schedule.length > 0 ? (
                upcoming_schedule.map((job, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800">{job.title}</h4>
                          <p className="text-sm text-slate-500">{job.client_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1 text-blue-600 font-medium">
                              <Calendar className="w-3 h-3" />
                              {new Date(job.date).toLocaleDateString('en-GB', { 
                                weekday: 'short', day: 'numeric', month: 'short'
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {job.time || 'TBC'}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {job.location || 'TBC'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-700">£{job.wage.toFixed(2)}</p>
                          <p className="text-xs text-slate-400">wage</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-slate-400">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No upcoming jobs scheduled</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffPerformanceDashboard;
